// components/cashSession/SessionHistory.js
import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Table, Badge, Button, Form,
  InputGroup, Alert, Spinner, Modal
} from 'react-bootstrap';
import {
  Lock, Unlock, Eye, Filter, RefreshCw, AlertTriangle,
  TrendingUp, TrendingDown, CheckCircle2, Clock
} from 'lucide-react';
import { cashSessionService } from '../../services/Cashsessionservice';
import CloseSessionModal from './Closesessionmodal';

/**
 * Vue manager : historique complet des sessions de caisse
 * Affiche toutes les sessions avec filtres et permet de forcer la fermeture
 */
const SessionHistory = () => {
  const [sessions, setSessions] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtres
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUserId, setFilterUserId] = useState('');

  // Détail / Force close
  const [selectedSession, setSelectedSession] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showForceCloseModal, setShowForceCloseModal] = useState(false);

  // Chargement initial
  useEffect(() => {
    loadAllData();
  }, []);

  // Recharger quand les filtres changent
  useEffect(() => {
    loadHistory();
  }, [filterStatus, filterUserId]);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([loadHistory(), loadActiveSessions()]);
    setLoading(false);
  };

  const loadHistory = async () => {
    try {
      const response = await cashSessionService.getHistory({
        status: filterStatus || undefined,
        userId: filterUserId || undefined,
        page: 0,
        size: 50
      });
      if (response.success) {
        setSessions(response.data || []);
      }
    } catch (e) {
      setError('Erreur de chargement');
    }
  };

  const loadActiveSessions = async () => {
    try {
      const response = await cashSessionService.getActiveSessions();
      if (response.success) {
        setActiveSessions(response.data || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleViewDetail = (session) => {
    setSelectedSession(session);
    setShowDetailModal(true);
  };

  const handleForceClose = (session) => {
    setSelectedSession(session);
    setShowForceCloseModal(true);
  };

  const handleForceCloseDone = () => {
    setShowForceCloseModal(false);
    setSelectedSession(null);
    loadAllData();
  };

  const fmt = cashSessionService.formatCurrency;
  const fmtDate = cashSessionService.formatDateTime;

  // Stats globales
  const totalRevenue = sessions
    .filter(s => s.status === 'CLOSED')
    .reduce((sum, s) => sum + (parseFloat(s.total_revenue) || 0), 0);

  const totalDifference = sessions
    .filter(s => s.status === 'CLOSED')
    .reduce((sum, s) => sum + (parseFloat(s.closing_difference) || 0), 0);

  return (
    <Container fluid className="py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">
          <Clock size={24} className="me-2" />
          Sessions de caisse
        </h3>
        <Button variant="outline-primary" size="sm" onClick={loadAllData}>
          <RefreshCw size={14} className="me-1" />
          Actualiser
        </Button>
      </div>

      {/* === Sessions actives (alerte) === */}
      {activeSessions.length > 0 && (
        <Alert variant="info" className="mb-3">
          <strong>🟢 {activeSessions.length} session(s) en cours :</strong>{' '}
          {activeSessions.map(s => (
            <Badge
              key={s.id}
              bg={s.status === 'LOCKED' ? 'warning' : 'success'}
              className="me-2"
            >
              {s.user_name} ({s.session_number})
              {s.status === 'LOCKED' && ' 🔒'}
            </Badge>
          ))}
        </Alert>
      )}

      {/* === Statistiques === */}
      <Row className="mb-3">
        <Col md={3} sm={6} className="mb-2">
          <Card className="h-100 border-success">
            <Card.Body>
              <small className="text-muted">Sessions totales</small>
              <h4 className="mb-0">{sessions.length}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-2">
          <Card className="h-100 border-primary">
            <Card.Body>
              <small className="text-muted">CA total (fermées)</small>
              <h4 className="mb-0">{fmt(totalRevenue)}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-2">
          <Card className="h-100 border-warning">
            <Card.Body>
              <small className="text-muted">En cours / Verrouillées</small>
              <h4 className="mb-0">{activeSessions.length}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-2">
          <Card className={`h-100 ${totalDifference < 0 ? 'border-danger' : totalDifference > 0 ? 'border-warning' : 'border-success'}`}>
            <Card.Body>
              <small className="text-muted">Écarts cumulés</small>
              <h4 className={`mb-0 ${totalDifference < 0 ? 'text-danger' : totalDifference > 0 ? 'text-warning' : 'text-success'}`}>
                {totalDifference > 0 && '+'}
                {fmt(totalDifference)}
              </h4>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* === Filtres === */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2">
            <Col md={4}>
              <InputGroup size="sm">
                <InputGroup.Text>
                  <Filter size={14} />
                </InputGroup.Text>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">Tous les statuts</option>
                  <option value="OPEN">🟢 Ouvertes</option>
                  <option value="LOCKED">🔒 Verrouillées</option>
                  <option value="CLOSED">⚫ Fermées</option>
                </Form.Select>
              </InputGroup>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* === Table sessions === */}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : sessions.length === 0 ? (
            <Alert variant="light" className="text-center m-3">
              Aucune session trouvée
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>N° Session</th>
                    <th>Vendeur</th>
                    <th>Statut</th>
                    <th>Ouverture</th>
                    <th>Fermeture</th>
                    <th>Durée</th>
                    <th className="text-end">Fond</th>
                    <th className="text-end">CA</th>
                    <th className="text-end">Écart</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => {
                    const badge = cashSessionService.getStatusBadge(s.status);
                    const diff = parseFloat(s.closing_difference);
                    const hasDiff = s.status === 'CLOSED' && !isNaN(diff) && Math.abs(diff) >= 1;

                    return (
                      <tr key={s.id}>
                        <td>
                          <code className="small">{s.session_number}</code>
                          {s.force_closed && (
                            <Badge bg="danger" className="ms-1" pill>
                              Forcée
                            </Badge>
                          )}
                        </td>
                        <td>{s.user_name}</td>
                        <td>
                          <Badge bg={badge.variant}>
                            {badge.icon} {badge.label}
                          </Badge>
                        </td>
                        <td className="small">{fmtDate(s.opened_at)}</td>
                        <td className="small">{s.closed_at ? fmtDate(s.closed_at) : '-'}</td>
                        <td className="small">
                          {cashSessionService.formatDuration(s.duration_minutes)}
                        </td>
                        <td className="text-end small">{fmt(s.opening_amount)}</td>
                        <td className="text-end">
                          <strong>{fmt(s.total_revenue)}</strong>
                          <br />
                          <small className="text-muted">{s.total_sales_count} vente(s)</small>
                        </td>
                        <td className="text-end">
                          {s.status === 'CLOSED' ? (
                            <span className={
                              diff < 0 ? 'text-danger fw-bold'
                              : diff > 0 ? 'text-warning fw-bold'
                              : 'text-success'
                            }>
                              {hasDiff && (diff > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />)}
                              {diff > 0 && '+'}
                              {fmt(s.closing_difference)}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="text-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewDetail(s)}
                            title="Voir détail"
                          >
                            <Eye size={14} />
                          </Button>
                          {s.status === 'LOCKED' && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="ms-1"
                              onClick={() => handleForceClose(s)}
                              title="Forcer la fermeture"
                            >
                              <Unlock size={14} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* === Modal Détail === */}
      {selectedSession && (
        <SessionDetailModal
          show={showDetailModal}
          session={selectedSession}
          onClose={() => setShowDetailModal(false)}
        />
      )}

      {/* === Modal Force Close === */}
      {selectedSession && (
        <CloseSessionModal
          show={showForceCloseModal}
          session={selectedSession}
          onClose={() => setShowForceCloseModal(false)}
          onSessionClosed={handleForceCloseDone}
          forceMode={true}
        />
      )}
    </Container>
  );
};

// ═══════════════════════════════════════════════════════════
// Sous-composant : Modal de détail d'une session
// ═══════════════════════════════════════════════════════════
const SessionDetailModal = ({ show, session, onClose }) => {
  const fmt = cashSessionService.formatCurrency;
  const badge = cashSessionService.getStatusBadge(session.status);

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <Eye size={18} className="me-2" />
          Détail session — {session.session_number}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col sm={6}>
            <small className="text-muted">Vendeur :</small>
            <div><strong>{session.user_name}</strong></div>
          </Col>
          <Col sm={6}>
            <small className="text-muted">Statut :</small>
            <div>
              <Badge bg={badge.variant}>{badge.icon} {badge.label}</Badge>
              {session.force_closed && <Badge bg="danger" className="ms-2">Forcée</Badge>}
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col sm={6}>
            <small className="text-muted">Ouverte :</small>
            <div>{cashSessionService.formatDateTime(session.opened_at)}</div>
          </Col>
          <Col sm={6}>
            <small className="text-muted">Fermée :</small>
            <div>{session.closed_at ? cashSessionService.formatDateTime(session.closed_at) : '-'}</div>
          </Col>
        </Row>

        {session.closed_by_name && (
          <Alert variant="light" className="py-2">
            <small>Fermée par : <strong>{session.closed_by_name}</strong></small>
          </Alert>
        )}

        <h6 className="mt-3">📊 Activité</h6>
        <Table size="sm" bordered>
          <tbody>
            <tr>
              <td>Nombre de ventes</td>
              <td className="text-end">{session.total_sales_count}</td>
            </tr>
            {session.total_cancelled_count > 0 && (
              <tr className="text-muted">
                <td>Ventes annulées</td>
                <td className="text-end">{session.total_cancelled_count}</td>
              </tr>
            )}
            <tr>
              <td>Chiffre d'affaires</td>
              <td className="text-end"><strong>{fmt(session.total_revenue)}</strong></td>
            </tr>
            {session.total_discount > 0 && (
              <tr className="text-muted">
                <td>Total remises</td>
                <td className="text-end">{fmt(session.total_discount)}</td>
              </tr>
            )}
          </tbody>
        </Table>

        <h6 className="mt-3">💳 Répartition par méthode</h6>
        <Table size="sm" bordered>
          <tbody>
            {[
              { label: '💵 Espèces', value: session.total_cash },
              { label: '📱 Wave', value: session.total_wave },
              { label: '📲 Orange Money', value: session.total_orange_money },
              { label: '💳 Carte', value: session.total_card },
              { label: '🏦 Virement', value: session.total_bank_transfer },
              { label: '📝 Chèque', value: session.total_check },
              { label: '⏳ Crédit', value: session.total_credit }
            ].filter(item => parseFloat(item.value) > 0).map((item, i) => (
              <tr key={i}>
                <td>{item.label}</td>
                <td className="text-end">{fmt(item.value)}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        {session.status === 'CLOSED' && (
          <>
            <h6 className="mt-3">🧮 Comptage caisse</h6>
            <Table size="sm" bordered>
              <tbody>
                <tr>
                  <td>Fond d'ouverture</td>
                  <td className="text-end">{fmt(session.opening_amount)}</td>
                </tr>
                <tr>
                  <td>+ Ventes espèces</td>
                  <td className="text-end">{fmt(session.total_cash)}</td>
                </tr>
                <tr>
                  <td>= Montant théorique</td>
                  <td className="text-end"><strong>{fmt(session.closing_amount_theoretical)}</strong></td>
                </tr>
                <tr>
                  <td>Montant compté</td>
                  <td className="text-end">{fmt(session.closing_amount_real)}</td>
                </tr>
                <tr className="table-active">
                  <td><strong>Écart</strong></td>
                  <td className={`text-end fw-bold ${
                    Math.abs(session.closing_difference) < 1 ? 'text-success'
                      : session.closing_difference < 0 ? 'text-danger' : 'text-warning'
                  }`}>
                    {session.closing_difference > 0 && '+'}
                    {fmt(session.closing_difference)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </>
        )}

        {session.opening_note && (
          <Alert variant="light">
            <small className="text-muted">Note d'ouverture :</small>
            <div>{session.opening_note}</div>
          </Alert>
        )}

        {session.closing_note && (
          <Alert variant="light">
            <small className="text-muted">Note de fermeture :</small>
            <div>{session.closing_note}</div>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Fermer</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SessionHistory;